import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { FencyProvider } from "@fencyai/react";
import { useTranslation } from "react-i18next";
import { FencyContentStatus, SignedDocumentDto, ViewDocumentAsOwnerDto } from "../../autogen/bff-api";
import { fencyPromise } from "../fency/fencySetup";
import { useFency } from "../fency/useFency";
import { useFencyDocumentSync } from "../fency/useFencyDocumentSync";
import { MemoryChat } from "./MemoryChat";

const getLoadingMessageKey = (contentStatus: FencyContentStatus | null): string => {
  if (contentStatus === FencyContentStatus.Synchronizing) return "FencyContentStatus_Synchronizing";
  if (contentStatus === FencyContentStatus.SynchronizationError)
    return "FencyContentStatus_SynchronizationError";
  if (contentStatus === FencyContentStatus.Empty) return "FencyContentStatus_Empty";
  return "FencyContentStatus_Synchronizing";
};

export const FencyChatWithDocumentModal = ({
  document,
  onClose,
  refetchDocuments,
}: {
  document: ViewDocumentAsOwnerDto | SignedDocumentDto;
  onClose: () => void;
  refetchDocuments?: () => void | Promise<void>;
}) => {
  const { t } = useTranslation();
  const { fetchCreateStreamClientToken, fetchCreateMemoryChatClientToken } = useFency();
  const { isReady, syncError, contentStatus } = useFencyDocumentSync(document, refetchDocuments);

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        maxW="960px"
        maxH="90vh"
        height="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>{t("Chat with document (Fency)")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6" flex={1} minH={0} overflow="hidden" display="flex" flexDirection="column">
          {isReady ? (
            <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
              <Flex flex={1} flexDirection="column" minH="0">
                <MemoryChat
                  fetchClientToken={() => fetchCreateMemoryChatClientToken(document)}
                  documentName={document.name}
                  emptyStateText={t("Ask something about this document.")}
                  placeholderText={t("Ask something about this document…")}
                />
              </Flex>
            </FencyProvider>
          ) : syncError ? (
            <Text color="red.500">{t("FencyContentStatus_SynchronizationError")}</Text>
          ) : (
            <Text>
              {t(getLoadingMessageKey(contentStatus))}
              {!syncError && <Spinner size="sm" ml="2" />}
            </Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
